import React, { FC, useCallback, useEffect, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TradeIn } from '@lib/suzuki-trade-in';
import { VehicleBrands } from '@lib/suzuki-trade-in/lib/components/TradeIn/interfaces';
import Collapse from 'antd/lib/collapse';
import cn from 'classnames';

import { Checkbox } from '@azgaz/azgaz-ui';
import { TBrandCar } from '@/selectors/getBrandCar';
import { Title, Icon, Tooltip } from '@/apps/appCreator/components';
import { actions as carActions } from '@/apps/appCreator/store/car';
import { config, format } from '@/utils';
import { getCarDiscounts, getCarTradeIn } from '@/selectors';
import { Disclaimers, TSortedDisclaimers } from '@/selectors/getCarDisclaimers';
import { TOOLTIP_TEXT } from '@/constants';

import '@lib/suzuki-trade-in/lib/style.css';

import style from './style.module.scss';

type TProps = {
  car: TBrandCar;
  disclaimers: TSortedDisclaimers;
};

type TStateTradeIn = {
  amount: number; // Если amount = 0 то трейд ин не расчитан
  mileage: number; // Это и все последующие свойства будут отсутствовать если amount = 0
  year: string;
  modification: { id: string; title: string };
  model: { id: string; title: string };
  brand: { id: string; title: string };
};

// TODO: заменить tradeInDomain для production
const CarTradeIn: FC<TProps> = ({ car, disclaimers }) => {
  const dispatch = useDispatch();
  const tradeInDomain = config.get('tradeInDomain');
  const { tradeInDiscount, resolvedTradeInDiscount } = car?.extra || {};
  const { tradeInPrice } = useSelector(getCarDiscounts);
  const { initialPayment } = useSelector(getCarTradeIn);

  const getIconContent = useCallback(() => <Icon name="circled-angle" />, []);

  const onChangeTradeIn = useCallback(state => {
    const { amount, brand, model } = state;
    dispatch(
      carActions.updateDiscountSelected({
        keyName: 'tradeInPrice',
        value: amount,
        selected: false,
      })
    );

    dispatch(carActions.updateTradeInBrand(brand?.id));
    dispatch(carActions.updateTradeInModel(model?.id));

    // сбрасываем первоначальный взнос, при смене авто trade-in
    if (!amount) {
      dispatch(carActions.updateTradeInInitialPayment(false));
    }
  }, []);

  const onChangeInitialPayment = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(carActions.updateTradeInInitialPayment(e.target.checked));
    },
    []
  );

  useEffect(() => {
    dispatch(
      carActions.updateDiscountSelected({
        keyName: 'resolvedTradeInDiscount',
        value: resolvedTradeInDiscount,
      })
    );
  }, [resolvedTradeInDiscount, tradeInPrice]);

  return (
    <div className="mb-5">
      <Collapse
        className={cn(style.wrap, 'collapse-default-theme')}
        expandIcon={getIconContent}
        expandIconPosition="right"
      >
        <Collapse.Panel
          key={1}
          forceRender
          header={
            <Title className="my-2" tag="h2">
              Используйте Trade-In
              {Boolean(tradeInDiscount) && (
                <small>
                  Выгода до {format.price(tradeInDiscount!)} руб.
                  <sup>{disclaimers.get(Disclaimers.tradein)?.sortOrder}</sup>
                </small>
              )}
            </Title>
          }
        >
          <TradeIn domain={tradeInDomain} onChange={onChangeTradeIn} tradeInDiscounts={{
            [VehicleBrands.other]: {
              discount: car?.extra.anotherBrandTradeInDiscount ?? 0
            },
            [VehicleBrands.suzuki]: {
              discount: car?.extra.tradeInDiscount ?? 0
            },
          }} />
          {tradeInPrice.value ? (
            <Checkbox
              className="mt-3"
              value={initialPayment}
              onChange={onChangeInitialPayment}
            >
              Использовать в качестве первоначального взноса
              <Tooltip title={TOOLTIP_TEXT.initialPayment} placement="bottom">
                <Icon name="info" className="ml-2" />
              </Tooltip>
            </Checkbox>
          ) : null}
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

export default CarTradeIn;
